// PlaybooksTab — Owner-only Admin-Sub-Tab „Playbooks".
// Feste Antwort-Grundlagen, die EVE in ihren eigenen Worten formuliert, sobald
// eine Nutzerfrage die Auslöser-Stichwörter trifft.
// Source-of-truth: mockups/14-playbooks/proposal.html + approved.md (Doc-approved).
// Self-contained: lädt selbst via useAuth-Token, hält eigenen lokalen State.

import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import {
  fetchPlaybooks,
  createPlaybook,
  updatePlaybook,
  deletePlaybook,
  type AdminPlaybook,
  type PlaybookInput,
} from '@/lib/admin-playbooks-api';
import { PlaybookRow } from './playbook-row';
import { PlaybookForm } from './playbook-form';

export function PlaybooksTab() {
  const { accessToken } = useAuth();
  const [playbooks, setPlaybooks] = useState<AdminPlaybook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = kein Editor offen · 'new' = Create · sonst id des editierten Playbooks.
  const [editing, setEditing] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await fetchPlaybooks(accessToken);
      setPlaybooks(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Aktiv-Toggle in der Liste — optimistisch, bei Fehler Rollback.
  async function toggleActive(pb: AdminPlaybook) {
    if (!accessToken) return;
    const next = !pb.active;
    setPlaybooks((prev) =>
      prev.map((p) => (p.id === pb.id ? { ...p, active: next } : p)),
    );
    try {
      await updatePlaybook(accessToken, pb.id, { active: next });
    } catch (e) {
      setPlaybooks((prev) =>
        prev.map((p) => (p.id === pb.id ? { ...p, active: pb.active } : p)),
      );
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
  }

  async function removePlaybook(pb: AdminPlaybook) {
    if (!accessToken) return;
    try {
      await deletePlaybook(accessToken, pb.id);
      setPlaybooks((prev) => prev.filter((p) => p.id !== pb.id));
      if (editing === pb.id) setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  }

  async function savePlaybook(input: PlaybookInput) {
    if (!accessToken) return;
    if (editing && editing !== 'new') {
      await updatePlaybook(accessToken, editing, input);
    } else {
      await createPlaybook(accessToken, input);
    }
    await reload();
    setEditing(null);
  }

  const editingPlaybook =
    editing && editing !== 'new' ? playbooks.find((p) => p.id === editing) ?? null : null;
  const activeCount = playbooks.filter((p) => p.active).length;

  return (
    <div className="space-y-5">
      {/* Header + „Neu" */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Playbooks</h2>
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-neutral-400 tabular">
              {playbooks.length} Playbooks · {activeCount} aktiv
            </span>
          </div>
          <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-neutral-500">
            Feste Antwort-Grundlagen — EVE formuliert sie in ihren eigenen Worten. Trifft eine
            Nutzerfrage die Auslöser-Stichwörter, antwortet EVE auf dieser Basis (nicht wörtlich,
            sondern im eigenen Ton).
          </p>
        </div>

        <button
          type="button"
          onClick={() => setEditing('new')}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-amber-500/50 bg-amber-500/20 px-4 py-2 text-[13px] font-semibold text-amber-200 transition hover:bg-amber-500/30"
        >
          <Plus className="size-4" />
          Neu
        </button>
      </section>

      {error ? (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : null}

      {/* Liste */}
      {loading ? (
        <div className="py-10 text-center text-[12px] text-neutral-500">Lade Playbooks…</div>
      ) : playbooks.length === 0 ? (
        <div className="glass-strong rounded-2xl border border-white/5 py-10 text-center text-[12px] text-neutral-500">
          Noch keine Playbooks. „Neu" legt die erste Antwort-Grundlage an.
        </div>
      ) : (
        <section className="space-y-3">
          {playbooks.map((pb) => (
            <PlaybookRow
              key={pb.id}
              playbook={pb}
              onEdit={() => setEditing(pb.id)}
              onToggle={() => void toggleActive(pb)}
              onDelete={() => void removePlaybook(pb)}
            />
          ))}
        </section>
      )}

      {/* Editor */}
      {editing ? (
        <PlaybookForm
          key={editing}
          accessToken={accessToken}
          playbook={editingPlaybook}
          onSave={savePlaybook}
          onCancel={() => setEditing(null)}
          onDelete={
            editingPlaybook ? () => void removePlaybook(editingPlaybook) : undefined
          }
        />
      ) : null}

      <div className="pb-2 text-center text-[11px] text-neutral-600">
        Playbooks wirken über alle EVE-Modi (Chat, Briefings, Lagebild) — sie sind Grundlage, kein
        Wortlaut.
      </div>
    </div>
  );
}
